import { cva, type VariantProps } from "class-variance-authority";
import type { ReactNode } from "react";

import { cn } from "../lib/cn";

export const skeletonVariants = cva(
  cn(
    "block bg-skeleton-base",
    // The pulse is essential feedback — it is the difference between "loading"
    // and "broken" — so it keeps running under reduced motion, unlike the
    // decorative transitions elsewhere in the kit.
    "animate-skeleton",
  ),
  {
    variants: {
      shape: {
        /** A line of body copy. */
        text: "h-4 rounded-sm",
        /** A screen or section heading. */
        title: "h-6 rounded-sm",
        /** A caption or a piece of metadata. */
        caption: "h-3 rounded-sm",
        /** An avatar or a team mark. */
        circle: "rounded-full",
        /** A control-height block — a button or an input placeholder. */
        control: "h-control-height rounded-sm",
        /** A whole card, for a list that is still loading. */
        card: "h-32 rounded-md",
      },
    },
    defaultVariants: { shape: "text" },
  },
);

export type typeSkeletonProps = VariantProps<typeof skeletonVariants> & {
  /**
   * Number of stacked lines. Only meaningful for the line shapes; the last
   * line is rendered short, the way a real paragraph ends.
   */
  lines?: number | undefined;
  /** A width utility class — `w-full`, `w-24`. Defaults to full width. */
  width?: string | undefined;
  /**
   * What is loading, announced once to a screen reader. Say the thing, not
   * "Loading" — "Loading your pools" tells a fan what to expect.
   */
  label?: string | undefined;
  className?: string | undefined;
};

/**
 * A placeholder shaped like the content that is coming.
 *
 * A section that is fetching shows a skeleton
 * shaped like ITS content — a skeleton card for a list of cards, a skeleton row
 * for a standings row. Never a generic spinner covering a screen that already
 * has other content on it, and never a full-screen blocking overlay.
 */
export const Skeleton = ({
  shape,
  lines = 1,
  width,
  label = "Loading",
  className,
}: typeSkeletonProps): ReactNode => {
  const isLineShape =
    shape === undefined || shape === "text" || shape === "caption";
  const count = isLineShape ? Math.max(1, lines) : 1;

  return (
    <div
      // `role="status"` with a polite live region: announced once, without
      // stealing focus from whatever the fan was doing.
      role="status"
      aria-live="polite"
      aria-busy="true"
      data-testid="skeleton"
      className={cn("flex w-full flex-col gap-2", className)}
    >
      {Array.from({ length: count }, (_, index) => (
        <span
          key={index}
          aria-hidden="true"
          className={cn(
            skeletonVariants({ shape }),
            width ?? "w-full",
            // A paragraph's last line is short. Matching that is what makes a
            // skeleton read as text rather than as a stack of gray bars.
            count > 1 && index === count - 1 ? "w-3/5" : undefined,
          )}
        />
      ))}
      <span className="sr-only">{label}</span>
    </div>
  );
};
